import React from 'react';
import styled, { css } from 'styled-components/macro';

import { ILabelValuePair } from 'types/shared';
import salesTypes from 'maps/sales-types';

import Menu, { MenuList, Figure } from 'components/base/Menu/Menu';
import Box from 'components/elements/Box';
import { ButtonContent } from 'components/base/Dropdown/shared/styles';
import { ButtonWrapper } from 'components/base/Dropdown/shared/styles';
import { ListItem, Button } from 'components/base/Dropdown/shared/components';
import { MenuRoot, MenuButton } from 'components/base/Menu/index';

const BaseButtonStyles = css`
  font-size: 11px;
  font-weight: 600;
  line-height: normal;
  padding: 0.75rem 1rem !important;
`;

const AlbumEquivalentsButton = styled('button')<any>`
  ${BaseButtonStyles}
  background-color: ${({ theme, isActive }): string =>
    isActive ? theme.AccentGreen : theme.White};
  color: ${({ theme, isActive }): string =>
    isActive ? theme.White : theme.DeepBlue};
  border: 1px solid ${({ theme }): string => theme.AccentGreen};
  border-top-left-radius: 3px;
  border-bottom-left-radius: 3px;
  font-size: 1rem;
  margin: 0;

  ${({ isMobile }): any =>
    isMobile &&
    css`
      @media screen and (max-width: 1199px) {
        flex-grow: 1;
        font-size: 1em;
      }
    `};

  ${({ isButtonNewDesigned, isActive }): any =>
    isButtonNewDesigned &&
    css`
      color: ${({ theme }): string =>
        isActive ? theme.White : theme.AccentGreen};
      border-right: initial;
      &:hover {
        background: ${({ theme }): string =>
          isActive ? theme.AccentGreen : '#f3fdfb'}; // 5% opacity
        border: 1px solid
          ${({ theme }): string => (isActive ? theme.AccentGreen : '#85ebd3')}; // 50% opacity
        border-right: initial;
        color: ${({ theme }): string =>
          isActive ? theme.White : theme.AccentGreen};
      }
      &:active {
        background: ${({ theme }): string =>
          isActive ? theme.AccentGreen : '#f3fdfb'}; // 15% opacity
        border-right: initial;
      }
      &:disabled {
        background: ${({ theme }): string => theme.LightGrey};
        color: #85ebd3;
        cursor: not-allowed;
      }
    `}
`;

const SalesTypeBox = styled(Box)<any>`
  ${MenuRoot} {
    margin: 0;

    &[data-menu-root] {
      @media screen and (min-width: 50em) {
        margin-left: 0;
      }
    }
    @media screen and (max-width: 1199px) {
      flex-grow: ${({ isMobile }): number => (isMobile ? 1 : 0)};
    }

    ${MenuButton} {
      &[aria-expanded='true'] .dropdown-chevron {
        transform: rotate(270deg);
      }
      &[value]:not([value='']) ${ButtonWrapper} {
        background-color: ${({ theme }): string => theme.AccentGreen};
        color: ${({ theme }): string => theme.White};

        & .dropdown-chevron {
          color: ${({ theme }): string => theme.White};
        }
      }
      &[value='']:focus ${ButtonWrapper}, &:focus ${ButtonWrapper} {
        background-color: ${({ theme }): string => theme.Sand};
      }

      ${ButtonWrapper} {
        font-size: 1em;
      }

      ${({ isRoundedDropdown, theme }): any =>
        isRoundedDropdown &&
        css`
          & > ${ButtonWrapper} {
            background-color: ${theme.White};
            color: ${theme.AccentGreen};
            min-height: 100%;
            border: 1px solid ${theme.AccentGreen};
            & > .dropdown-chevron {
              color: ${theme.AccentGreen};
            }
          }
          &[value='']:hover
            ${ButtonWrapper},
            &:hover
            ${ButtonWrapper},
            &[value='']:focus
            ${ButtonWrapper},
            &:focus
            ${ButtonWrapper} {
            background-color: #f3fdfb;
          }
          &:disabled & {
            background: ${theme.LightGrey};
            color: #85ebd3;
            cursor: not-allowed;
          }
        `}
    }
    ${MenuList} {
      box-shadow: none;
    }
  }

  ${ButtonWrapper} {
    ${BaseButtonStyles}
    ${({ isMobile }): any =>
      isMobile &&
      css`
        @media screen and (max-width: 1199px) {
          font-size: 1em;
          min-height: 100%;
          padding: 1.5em 1.5em !important;
        }
      `};
    color: ${({ theme }): string => theme.NightBlue};

    & > .dropdown-chevron {
      color: ${({ theme }): string => theme.NightBlue};
      transform: rotate(90deg);
    }

    ${ButtonContent} {
      flex: 0 1 auto;
    }
  }
  ${Figure} {
    background-color: ${({ theme }): string => theme.White};
    width: 200px;
    right: 0;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
  }
`;

const Label = styled('p')`
  color: ${({ theme }): string => theme.CasperGrey};
  text-transform: uppercase;
  font-size: 11px;
  padding-left: ${({ theme }): string => theme.spacing.medium};
`;

const ListLabel: React.FC = () => <Label>View</Label>;

interface IOptionType {
  label: string;
  value: string;
}

interface ISalesTypeDropdownProps {
  allSalesOptions: ILabelValuePair<string>[];
  browseEntityType: string;
  salesType: ILabelValuePair<string>;
  saveComponentSettings: Function;
  isExternalUser: boolean;
  isMobile?: boolean;
  isRoundedDropdown?: string;
}

const SalesTypeDropdown: React.FC<ISalesTypeDropdownProps> = (props) => {
  const {
    allSalesOptions,
    browseEntityType,
    salesType,
    saveComponentSettings,
    isExternalUser,
    isMobile,
    isRoundedDropdown,
  } = props;

  const salesTypesWithoutAlbum = allSalesOptions.filter(
    (option: IOptionType) => option.value !== salesTypes.equivalents.value
  );

  const selectedDropdownItem =
    salesTypesWithoutAlbum.filter(
      (option: IOptionType) => option.value === salesType.value
    ).length > 0
      ? salesType
      : null;

  return (
    <SalesTypeBox
      display="flex"
      justifyContent="flex-end"
      isMobile={isMobile}
      isRoundedDropdown={isRoundedDropdown}
    >
      {!isExternalUser && (
        <AlbumEquivalentsButton
          type="button"
          isActive={salesType.value === salesTypes.equivalents.value}
          isMobile={isMobile}
          onClick={(): void =>
            saveComponentSettings({
              selectedSalesType: salesTypes.equivalents.value,
              browseEntityType,
            })
          }
          isButtonNewDesigned
        >
          {salesTypes.equivalents.label}
        </AlbumEquivalentsButton>
      )}

      <Menu
        items={salesTypesWithoutAlbum}
        selectedItem={selectedDropdownItem}
        onChange={(option: IOptionType): void =>
          saveComponentSettings({
            selectedSalesType: option.value,
            browseEntityType,
          })
        }
        ListLabel={ListLabel}
        Item={ListItem}
        Button={Button}
        defaultButton={{ label: 'Streams', value: 'streams' }}
        isRoundedDropdown={isRoundedDropdown}
      />
    </SalesTypeBox>
  );
};

export default SalesTypeDropdown;
